import { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
    IconChevronLeft, IconDownload, IconX, IconTrash, IconRefresh, IconPlus,
    IconFileText, IconPhoto, IconFileSpreadsheet, IconFileZip,
    IconCurrencyDollar, IconUser, IconCalendarEvent, IconClipboardList,
    IconPaperclip, IconTimeline, IconBuilding,
} from '@tabler/icons-react';
import toast from 'react-hot-toast';
import Breadcrumb from '../../components/common/Breadcrumb';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import { PageLoader } from '../../components/common/Loader';
import { useAuth } from '../../contexts/AuthContext';
import {
    useGrantProposal,
    useUploadGrantAttachments,
    useDownloadGrantAttachment,
    useReplaceGrantAttachment,
    useDeleteGrantAttachment,
} from '../../hooks/useGrants';

// ── Helpers ────────────────────────────────────────────────────────────────

const STATUS_STYLE = {
    draft:           'bg-bg-light text-text-secondary border border-border',
    internal_review: 'bg-sky-500/10 text-sky-500 border border-sky-500/30',
    submitted:       'bg-amber-500/10 text-amber-500 border border-amber-500/30',
    awarded:         'bg-success/10 text-success border border-success/30',
    rejected:        'bg-danger/10 text-danger border border-danger/30',
};

const STATUS_DOT = {
    draft:           '#94a3b8',
    internal_review: '#0ea5e9',
    submitted:       '#f59e0b',
    awarded:         '#22c55e',
    rejected:        '#ef4444',
};

const STATUS_SHORT = {
    draft:           'Draft',
    internal_review: 'In Review',
    submitted:       'Submitted',
    awarded:         'Awarded',
    rejected:        'Rejected',
};

const AVATAR_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#f43f5e', '#06b6d4'];

function getInitials(name) {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

function MiniAvatar({ name }) {
    const bg = AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];
    return (
        <div style={{ backgroundColor: bg }}
            className="w-[26px] h-[26px] rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
            {getInitials(name)}
        </div>
    );
}

function formatHistoryDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        + ' · '
        + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function formatBytes(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
const SHEET_EXTS = ['xlsx', 'xls', 'csv'];
const ZIP_EXTS   = ['zip', 'rar', '7z'];

function getExt(filename) { return (filename || '').split('.').pop().toLowerCase(); }

function getFileIcon(filename) {
    const ext = getExt(filename);
    if (IMAGE_EXTS.includes(ext)) return IconPhoto;
    if (SHEET_EXTS.includes(ext)) return IconFileSpreadsheet;
    if (ZIP_EXTS.includes(ext))   return IconFileZip;
    return IconFileText;
}

function canPreview(filename) {
    const ext = getExt(filename);
    return IMAGE_EXTS.includes(ext) || ext === 'pdf';
}

// ── Sub-components ────────────────────────────────────────────────────────

function StatusPill({ status }) {
    const cls      = STATUS_STYLE[status] || STATUS_STYLE.draft;
    const dotColor = STATUS_DOT[status]   || STATUS_DOT.draft;
    const label    = STATUS_SHORT[status] || status;
    return (
        <span className={`inline-flex items-center gap-5 px-8 py-3 rounded-full text-[11px] font-semibold whitespace-nowrap ${cls}`}>
            <span className="w-[6px] h-[6px] rounded-full flex-shrink-0" style={{ backgroundColor: dotColor }} />
            {label}
        </span>
    );
}

function Card({ title, icon: Icon, color = '#6366f1', children, badge, action }) {
    return (
        <div className="bg-bg-dark rounded-xl shadow-light border border-border overflow-hidden">
            <div className="px-20 py-12 border-b border-border flex items-center gap-8">
                <Icon size={14} style={{ color }} />
                <h3 className="text-sm font-bold text-text-main flex-1">{title}</h3>
                {action}
                {badge != null && (
                    <span className="text-xs font-normal bg-bg-light text-text-secondary px-8 py-3 rounded-full border border-border">
                        {badge}
                    </span>
                )}
            </div>
            <div className="p-20">{children}</div>
        </div>
    );
}

function InfoRow({ label, value, icon: Icon }) {
    if (!value && value !== 0) return null;
    return (
        <div className="flex items-center justify-between py-8 border-b border-border/50 last:border-0">
            <div className="flex items-center gap-6">
                {Icon && <Icon size={12} className="text-text-secondary flex-shrink-0" />}
                <span className="text-xs text-text-secondary">{label}</span>
            </div>
            <span className="text-sm font-semibold text-text-main text-right max-w-[55%] truncate">{value}</span>
        </div>
    );
}

function QuickStat({ icon: Icon, label, value, accent }) {
    return (
        <div className="bg-bg-dark rounded-xl shadow-light border border-border p-20 flex items-center gap-15">
            <div className="w-[44px] h-[44px] rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${accent}1a` }}>
                <Icon size={20} style={{ color: accent }} />
            </div>
            <div className="min-w-0">
                <p className="text-text-secondary text-xs uppercase tracking-wider mb-3">{label}</p>
                <p className="text-[17px] font-bold truncate leading-snug">{value ?? '—'}</p>
            </div>
        </div>
    );
}

function EmptyState({ text }) {
    return <p className="text-xs text-text-secondary text-center py-15">{text}</p>;
}

function FilePreviewModal({ attachment, onClose }) {
    const { file_url, original_filename } = attachment;
    const isImg = IMAGE_EXTS.includes(getExt(original_filename));
    const isPDF = getExt(original_filename) === 'pdf';

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex flex-col" onClick={onClose}>
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
            <div className="relative z-10 flex flex-col h-full" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-20 py-12 bg-bg-dark border-b border-border flex-shrink-0">
                    <span className="text-sm font-medium truncate max-w-[60%]">{original_filename}</span>
                    <div className="flex items-center gap-8 flex-shrink-0">
                        <a href={file_url} target="_blank" rel="noopener noreferrer"
                            className="btn btn-outline-secondary flex items-center gap-5 text-xs"
                            onClick={e => e.stopPropagation()}>
                            <IconDownload size={13} /> Download
                        </a>
                        <button onClick={onClose}
                            className="p-6 rounded-lg hover:bg-danger/10 text-text-secondary hover:text-danger transition-colors">
                            <IconX size={16} />
                        </button>
                    </div>
                </div>
                <div className="flex-1 overflow-auto flex items-center justify-center p-20">
                    {isImg && (
                        <img src={file_url} alt={original_filename}
                            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
                    )}
                    {isPDF && (
                        <iframe src={file_url} title={original_filename}
                            className="w-full h-full rounded-lg bg-white" style={{ minHeight: '75vh' }} />
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}

// ── Main Page ─────────────────────────────────────────────────────────────

export default function GrantProposalDetail() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { user, hasAnyPermission } = useAuth();
    const orgId = user?.orgId;
    const isSuperuser = user?.is_superuser;
    const canEditAttachments = isSuperuser || hasAnyPermission(['rbacapp.gp_draft', 'rbacapp.gp_edit_review']);

    const { data: proposal, isLoading } = useGrantProposal(id, orgId);
    const [previewAtt, setPreviewAtt] = useState(null);
    const [replacingAtt, setReplacingAtt] = useState(null);
    const [attToDelete, setAttToDelete] = useState(null);
    const replaceInputRef = useRef(null);
    const uploadInputRef  = useRef(null);

    const uploadAttachments  = useUploadGrantAttachments();
    const downloadAttachment = useDownloadGrantAttachment();
    const replaceAttachment  = useReplaceGrantAttachment();
    const deleteAttachment   = useDeleteGrantAttachment();

    const handleDownload = (att) => {
        downloadAttachment.mutate({ proposalId: id, attachmentId: att.id, filename: att.original_filename, orgId });
    };

    const handleAddDocuments = async (e) => {
        const files = Array.from(e.target.files).map(file => ({ file }));
        if (!files.length) return;
        e.target.value = '';
        try {
            await uploadAttachments.mutateAsync({ id, files, orgId });
            toast.success(`${files.length} document(s) added successfully`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to add documents');
        }
    };

    const handleReplaceClick = (att) => {
        setReplacingAtt(att);
        replaceInputRef.current?.click();
    };

    const handleReplaceFile = async (e) => {
        const file = e.target.files[0];
        if (!file || !replacingAtt) return;
        e.target.value = '';
        try {
            await replaceAttachment.mutateAsync({ proposalId: id, attachmentId: replacingAtt.id, file, orgId });
            toast.success('File replaced successfully');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to replace file');
        } finally {
            setReplacingAtt(null);
        }
    };

    const confirmDeleteAttachment = async () => {
        if (!attToDelete) return;
        try {
            await deleteAttachment.mutateAsync({ proposalId: id, attachmentId: attToDelete.id, orgId });
            toast.success('Attachment deleted');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to delete attachment');
        } finally {
            setAttToDelete(null);
        }
    };

    if (isLoading) return <PageLoader />;
    if (!proposal) return (
        <div className="flex items-center justify-center py-60 text-text-secondary text-sm">
            Proposal not found.
        </div>
    );

    const attachments = proposal.attachments || [];
    const history = proposal.status_history || [];
    const fundingDisplay = proposal.funding_amount
        ? `${proposal.currency} ${Number(proposal.funding_amount).toLocaleString()}`
        : null;

    return (
        <>
            <Breadcrumb title="Proposal Detail" path="Project Management / Grant & Proposal / Detail" />

            <button type="button" onClick={() => navigate(-1)}
                className="btn btn-outline-secondary flex items-center gap-6 mb-15">
                <IconChevronLeft size={15} /> Back
            </button>

            {/* ── Profile Header */}
            <div className="bg-bg-dark rounded-xl shadow-light border border-border p-20 mb-20">
                <div className="flex items-start gap-15 flex-wrap justify-between">
                    <div className="flex items-start gap-15 min-w-0">
                        <div className="w-[52px] h-[52px] rounded-xl flex items-center justify-center flex-shrink-0 bg-primary/10">
                            <IconClipboardList size={24} className="text-primary" />
                        </div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-10 flex-wrap mb-5">
                                <h2 className="text-[17px] font-bold text-text-main leading-snug">{proposal.title}</h2>
                                <StatusPill status={proposal.status} />
                            </div>
                            <p className="text-sm text-text-secondary flex items-center gap-5">
                                <IconBuilding size={13} className="flex-shrink-0" />
                                {proposal.client_donor}
                            </p>
                        </div>
                    </div>
                    <div className="text-xs text-text-secondary flex-shrink-0 font-mono bg-bg-light border border-border px-10 py-5 rounded">
                        GP-{String(proposal.id || '').slice(0, 8).toUpperCase()}
                    </div>
                </div>
            </div>

            {/* ── Quick Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-15 mb-20">
                <QuickStat icon={IconCurrencyDollar} label="Funding Amount" value={fundingDisplay || '—'} accent="#22c55e" />
                <QuickStat icon={IconUser}           label="Lead Person"    value={proposal.lead_person || '—'} accent="#6366f1" />
                <QuickStat icon={IconCalendarEvent}  label="Deadline"       value={proposal.deadline || '—'} accent="#f59e0b" />
                <QuickStat icon={IconPaperclip}      label="Documents"      value={attachments.length} accent="#0ea5e9" />
            </div>

            <div className="space-y-15">

                {/* ── Proposal Details */}
                <Card title="Proposal Details" icon={IconClipboardList} color="#6366f1">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-40">
                        <InfoRow label="Grant Type"          value={proposal.grant_type_display} />
                        <InfoRow label="Funding Amount"      value={fundingDisplay} />
                        <InfoRow label="Required Investment" value={proposal.required_investment ? `${proposal.currency} ${Number(proposal.required_investment).toLocaleString()}` : null} />
                        <InfoRow label="Lead Person"         value={proposal.lead_person} icon={IconUser} />
                        <InfoRow label="Deadline"            value={proposal.deadline} icon={IconCalendarEvent} />
                        <InfoRow label="Date of Submission"  value={proposal.date_of_submission} />
                        <InfoRow label="Date of Response"    value={proposal.date_of_response} />
                        <InfoRow label="Outcome"             value={proposal.outcome_display} />
                        <InfoRow label="Created By"          value={proposal.created_by_name || null} icon={IconUser} />
                        <InfoRow label="Created At"          value={proposal.created_at ? new Date(proposal.created_at).toLocaleDateString() : null} icon={IconCalendarEvent} />
                    </div>

                    {proposal.project_domains?.length > 0 && (
                        <div className="mt-15 pt-15 border-t border-border">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-text-secondary mb-10">Project Domains</p>
                            <div className="flex flex-wrap gap-6">
                                {proposal.project_domains.map(d => (
                                    <span key={d} className="text-xs bg-primary/10 text-primary border border-primary/20 px-10 py-4 rounded-full">{d}</span>
                                ))}
                            </div>
                        </div>
                    )}
                </Card>

                {/* ── Narrative (only shown if any field has content) */}
                {(proposal.description || proposal.business_development || proposal.reason_for_outcome) && (
                    <Card title="Narrative" icon={IconFileText} color="#f59e0b">
                        <div className="space-y-20">
                            {proposal.description && (
                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-text-secondary mb-8">Description</p>
                                    <p className="text-sm leading-relaxed whitespace-pre-wrap text-text-main">{proposal.description}</p>
                                </div>
                            )}
                            {proposal.business_development && (
                                <div className="pt-15 border-t border-border">
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-text-secondary mb-8">Business Development</p>
                                    <p className="text-sm leading-relaxed whitespace-pre-wrap text-text-main">{proposal.business_development}</p>
                                </div>
                            )}
                            {proposal.reason_for_outcome && (
                                <div className="pt-15 border-t border-border">
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-text-secondary mb-8">Reason for Outcome</p>
                                    <p className="text-sm leading-relaxed whitespace-pre-wrap text-text-main">{proposal.reason_for_outcome}</p>
                                </div>
                            )}
                        </div>
                    </Card>
                )}

                {/* ── Documents */}
                <Card
                    title="Documents"
                    icon={IconPaperclip}
                    color="#0ea5e9"
                    badge={attachments.length || null}
                    action={canEditAttachments && (
                        <button
                            onClick={() => uploadInputRef.current?.click()}
                            disabled={uploadAttachments.isPending}
                            className="flex items-center gap-5 px-10 py-5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors text-xs font-semibold">
                            <IconPlus size={13} />
                            {uploadAttachments.isPending ? 'Adding...' : 'Add Documents'}
                        </button>
                    )}
                >
                    {attachments.length === 0 ? (
                        <EmptyState text="No documents uploaded yet." />
                    ) : (
                        <ul className="space-y-8">
                            {attachments.map(att => {
                                const FileIcon = getFileIcon(att.original_filename);
                                const previable = canPreview(att.original_filename);
                                const meta = [
                                    formatBytes(att.file_size),
                                    att.uploaded_by_name || null,
                                    att.uploaded_at ? new Date(att.uploaded_at).toLocaleDateString() : null,
                                ].filter(Boolean).join(' · ');

                                return (
                                    <li key={att.id} className="flex items-center justify-between gap-10 bg-bg-light border border-border rounded-lg px-15 py-10 hover:border-primary/40 transition-colors">
                                        <div className="flex items-center gap-10 min-w-0">
                                            <FileIcon size={15} className="text-primary flex-shrink-0" />
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium truncate">{att.original_filename}</p>
                                                {meta && <p className="text-xs text-text-secondary mt-1">{meta}</p>}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-6 flex-shrink-0">
                                            <button
                                                onClick={() => handleDownload(att)}
                                                disabled={downloadAttachment.isPending}
                                                className="flex items-center gap-5 px-10 py-5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors text-xs font-semibold"
                                                title="Download">
                                                <IconDownload size={13} /> Download
                                            </button>
                                            {canEditAttachments && (
                                                <>
                                                    <button
                                                        onClick={() => handleReplaceClick(att)}
                                                        disabled={replaceAttachment.isPending && replacingAtt?.id === att.id}
                                                        className="flex items-center gap-5 px-10 py-5 rounded-lg bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 transition-colors text-xs font-semibold"
                                                        title="Replace file">
                                                        <IconRefresh size={13} /> Replace
                                                    </button>
                                                    <button
                                                        onClick={() => setAttToDelete(att)}
                                                        className="flex items-center gap-5 px-10 py-5 rounded-lg bg-danger/10 text-danger hover:bg-danger/20 transition-colors text-xs font-semibold"
                                                        title="Delete">
                                                        <IconTrash size={13} /> Delete
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </Card>

                {/* ── Status History */}
                {history.length > 0 && (
                    <Card title="Status History" icon={IconTimeline} color="#14b8a6" badge={history.length}>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr>
                                        <th className="text-left text-[10px] font-bold uppercase tracking-widest text-text-secondary pb-10 pr-20">Transition</th>
                                        <th className="text-left text-[10px] font-bold uppercase tracking-widest text-text-secondary pb-10 pr-20">Changed By</th>
                                        <th className="text-left text-[10px] font-bold uppercase tracking-widest text-text-secondary pb-10 pr-20">Date & Time</th>
                                        <th className="text-left text-[10px] font-bold uppercase tracking-widest text-text-secondary pb-10">Comment</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map((entry) => (
                                        <tr key={entry.id} className="border-t border-border/50 hover:bg-bg-light/40 transition-colors">
                                            <td className="py-10 pr-20">
                                                <div className="flex items-center gap-6 flex-nowrap">
                                                    <StatusPill status={entry.from_status} />
                                                    <span className="text-[10px] text-text-secondary font-bold flex-shrink-0">→</span>
                                                    <StatusPill status={entry.to_status} />
                                                </div>
                                            </td>
                                            <td className="py-10 pr-20">
                                                {entry.changed_by_name ? (
                                                    <div className="flex items-center gap-6">
                                                        <MiniAvatar name={entry.changed_by_name} />
                                                        <span className="text-xs text-text-secondary whitespace-nowrap">{entry.changed_by_name}</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-text-secondary">—</span>
                                                )}
                                            </td>
                                            <td className="py-10 pr-20">
                                                <span className="text-xs text-text-secondary whitespace-nowrap">{formatHistoryDate(entry.changed_at)}</span>
                                            </td>
                                            <td className="py-10">
                                                {entry.comment
                                                    ? <span className="text-xs text-text-secondary">{entry.comment}</span>
                                                    : <span className="text-xs text-text-secondary/40">—</span>
                                                }
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                )}

            </div>

            {previewAtt && (
                <FilePreviewModal attachment={previewAtt} onClose={() => setPreviewAtt(null)} />
            )}

            <input
                ref={replaceInputRef}
                type="file"
                className="hidden"
                onChange={handleReplaceFile}
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.xlsx,.xls,.ppt,.pptx,.csv,.txt,.zip,.rar"
            />
            <input
                ref={uploadInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleAddDocuments}
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.xlsx,.xls,.ppt,.pptx,.csv,.txt,.zip,.rar"
            />

            <ConfirmDeleteModal
                isOpen={!!attToDelete}
                onClose={() => setAttToDelete(null)}
                onConfirm={confirmDeleteAttachment}
                itemName={attToDelete?.original_filename}
                isLoading={deleteAttachment.isPending}
            />
        </>
    );
}
